/**
 * Prompt Templates Service
 *
 * Manages prompt templates for the homepage prompt library.
 * All prompts are global. Only admins can create/edit/delete.
 */

const MANAGER_BASE = '/api/manager';

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse(response) {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${response.status})`);
  }
  // 204 No Content
  if (response.status === 204) return null;
  return await response.json();
}

const promptTemplatesService = {
  // ── Template CRUD ────────────────────────────────────────────────────

  async listTemplates(filterType = null) {
    const params = filterType ? `?filter_type=${filterType}` : '';
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/${params}`, { headers: getAuthHeaders() })
    );
  },

  async getTemplate(templateId) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/${templateId}`, { headers: getAuthHeaders() })
    );
  },

  async createTemplate(templateData) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(templateData),
      })
    );
  },

  async updateTemplate(templateId, updateData) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/${templateId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      })
    );
  },

  async deleteTemplate(templateId) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
    );
  },

  // ── Import / Export ──────────────────────────────────────────────────

  async exportTemplates() {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/export`, { headers: getAuthHeaders() })
    );
  },

  async importTemplates(templates, overwrite = false) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/import?overwrite=${overwrite}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(templates),
      })
    );
  },

  // ── Folder management ────────────────────────────────────────────────

  async listFolders(includeUnpublished = false) {
    const params = includeUnpublished ? '?include_unpublished=true' : '';
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/folders${params}`, { headers: getAuthHeaders() })
    );
  },

  async createFolder(data) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/folders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      })
    );
  },

  async updateFolder(folderId, data) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/folders/${folderId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      })
    );
  },

  async deleteFolder(folderId) {
    return handleResponse(
      await fetch(`${MANAGER_BASE}/api/prompt-templates/folders/${folderId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
    );
  },
};

export default promptTemplatesService;